import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { arrowSeparatorMessage } from 'LuxKit/intl/separators'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'

const CardClickable = styled(Clickable)`
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const HeroContainer = styled.div`
  overflow: hidden;
`

const FallbackHero = styled.div`
  aspect-ratio: 3 / 2;
  background: linear-gradient(135deg,
    ${props => props.theme.palette.highlight.primary.normalForeground},
    ${props => props.theme.palette.highlight.secondary.normalBackground}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

const PlaneIconWrapper = styled.div`
  opacity: 0.3;
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
`

const ContentContainer = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-top: none;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${rem(12)};
`

interface Props {
  originCode: string
  originCityName: string
  destinationCode: string
  destinationCityName: string
  to: string
  price?: number
  departDate?: string
  returnDate?: string
  loading?: boolean
  imageId?: string
}

function PopularFlightCard(props: Props) {
  const { originCode, originCityName, destinationCode, destinationCityName, to, price, departDate, returnDate, loading, imageId } = props
  const intl = useIntl()

  const dateLabel = departDate && returnDate
    ? `${moment(departDate).format(DAY_MONTH_NAME_SHORT)} - ${moment(returnDate).format(DAY_MONTH_NAME_SHORT)}`
    : undefined

  return (
    <CardClickable to={to}>
      {imageId && (
        <HeroContainer>
          <Image image={{ id: imageId }} dpr={2} width={400} fit="center" aspectRatio="3:2" />
        </HeroContainer>
      )}
      {!imageId && (
        <FallbackHero>
          <PlaneIconWrapper>
            <LinePlaneDepartureIcon size="L" colour="neutral-eight" />
          </PlaneIconWrapper>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Heading variant="heading2" colour="neutral-eight">{originCode}</Heading>
            <BodyText variant="large" colour="neutral-eight">{intl.formatMessage(arrowSeparatorMessage)}</BodyText>
            <Heading variant="heading2" colour="neutral-eight">{destinationCode}</Heading>
          </Group>
        </FallbackHero>
      )}

      <ContentContainer>
        <VerticalSpacer gap={4}>
          <BodyText variant="large" weight="bold">
            {intl.formatMessage(
              {
                id: 'KcL8VPQLg2',
                defaultMessage: '{originCity} ({originCode}) to {destCity} ({destCode})',
                description: 'Route label on popular flight card showing origin and destination',
              },
              { originCity: originCityName, originCode, destCity: destinationCityName, destCode: destinationCode },
            )}
          </BodyText>
          {dateLabel && (
            <BodyText variant="medium" colour="neutral-three">
              {dateLabel}
            </BodyText>
          )}
        </VerticalSpacer>

        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <div>
            {loading && <TextLoadingBox width="12ch" />}
            {!loading && price != null && (
              <>
                <PriceRowPriceCaption>
                  {intl.formatMessage({
                    id: 'G65No1IDdY',
                    defaultMessage: 'Economy return from',
                    description: 'Price caption on popular flight card indicating 7 day return price',
                  })}
                </PriceRowPriceCaption>
                <PriceRowPrice
                  price={price}
                  saleUnit="person"
                  size="M"
                />
              </>
            )}
          </div>
          <TextButton kind="secondary" nonInteractable>
            {intl.formatMessage({
              id: '8jNUneFhRP',
              defaultMessage: 'Search',
              description: 'Button text to search for popular flight in the flights flow',
            })}
          </TextButton>
        </Group>
      </ContentContainer>
    </CardClickable>
  )
}

export default React.memo(PopularFlightCard)
